<template>
  <component :is="layoutComponent" />
  <Toast />
  <SmartAppBanner />
  <AppUpdateDialog />
</template>

<script setup lang="ts">
import { computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import { api } from '@/services/api'
import { useFirebaseMessaging } from '@/composables/useFirebaseMessaging'
import { isNativePlatform } from '@/utils/platform'
import { mergeGuestPrefsToServer } from '@/services/prayerPreferences'
import {
  bootstrapPrayerNotifications,
} from '@/services/prayerNotificationScheduler'
import { useGeolocation } from '@/composables/useGeolocation'
import { useRouteSeo } from '@/composables/useRouteSeo'

import MainLayout from '@/layouts/MainLayout.vue'
import AuthLayout from '@/layouts/AuthLayout.vue'
import Toast from '@/components/common/Toast.vue'
import SmartAppBanner from '@/components/common/SmartAppBanner.vue'
import AppUpdateDialog from '@/components/common/AppUpdateDialog.vue'

const route = useRoute()
const authStore = useAuthStore()
const { locale } = useI18n()
const { registerDeviceToken, initialize: initFirebase } = useFirebaseMessaging()
const geo = useGeolocation()
useRouteSeo()

async function syncPrayerNotificationsAfterAuth(): Promise<void> {
  if (!isNativePlatform) return
  try {
    await geo.locate()
    await mergeGuestPrefsToServer(geo.latitude.value, geo.longitude.value)
    await bootstrapPrayerNotifications(
      async () => ({ lat: geo.latitude.value, lng: geo.longitude.value }),
      true,
      locale.value,
    )
  } catch (error) {
    console.warn('Prayer notification sync after auth failed:', error)
  }
}

function canRegisterPush(): boolean {
  if (isNativePlatform) return true
  return typeof Notification !== 'undefined' && Notification.permission !== 'denied'
}

async function setupPushNotifications(): Promise<void> {
  await initFirebase()
  if (canRegisterPush()) {
    await registerDeviceToken()
  }
}

watch(locale, (val) => {
  const html = document.documentElement
  html.setAttribute('lang', val === 'ar' ? 'ar' : 'en')
  html.setAttribute('dir', val === 'ar' ? 'rtl' : 'ltr')
}, { immediate: true })

const layoutComponent = computed(() => {
  const layoutName = route.meta?.layout || 'main'
  switch (layoutName) {
    case 'auth':
      return AuthLayout
    case 'main':
    default:
      return MainLayout
  }
})

onMounted(async () => {
  // Restore user from API if we have token but no user (e.g. page refresh before we persisted user)
  if (authStore.isAuthenticated && !authStore.user) {
    try {
      const { data } = await api.me()
      if (data.success && data.data) {
        authStore.setUser({
          id: data.data.id,
          name: data.data.name,
          email: data.data.email,
          preferredLanguage: data.data.preferredLanguage,
          roles: data.data.roles || [],
        })
      }
    } catch {
      // 401 handled by api interceptor (clears token and redirects to login)
    }
  }

  // Initialize Firebase messaging and register device token for push notifications
  if (authStore.isAuthenticated) {
    void setupPushNotifications()
  }
})

// When user logs in (e.g. from login page), register device token
watch(() => authStore.isAuthenticated, async (authenticated) => {
  if (authenticated) {
    await setupPushNotifications()
    await syncPrayerNotificationsAfterAuth()
  }
})
</script>
